import React, { useMemo, useState } from "react";
import "./RsvpSpinComponent.scss";
import ButtonComponent from "../ButtonComponent/ButtonComponent";

const deg2rad = (d) => (d * Math.PI) / 180;

const RsvpSpinComponent = ({
    size = 320,
    to = "/rsvp",
    onComplete,
    sections = [
        "Let's Celebrate!",
        "You're Invited!",
        "Save the Date",
        "Spin Again",
        "Cheers!",
        "RSVP Now!",
    ],
    forceWin = true,
    spinsMin = 5,
    spinsMax = 7,
    alignBias = 3,          // tiny nudge right
    spinDuration = 9,       // slow spin (s)
}) => {
    const [rotation, setRotation] = useState(0);
    const [spinning, setSpinning] = useState(false);
    const [landed, setLanded] = useState(false);

    const cx = size / 2;
    const cy = size / 2;
    const padding = 14;
    const r = cx - padding;

    const sliceAngle = 360 / sections.length;
    const rsvpIndex = useMemo(() => {
        const idx = sections.findIndex(s => s.trim().toLowerCase() === "rsvp now!");
        return idx >= 0 ? idx : sections.length - 1;
    }, [sections]);

    const polar = (radius, angDeg) => {
        const a = deg2rad(angDeg);
        return { x: cx + radius * Math.cos(a), y: cy + radius * Math.sin(a) };
    };

    const wedgePath = (startDeg, endDeg) => {
        const p1 = polar(r, startDeg);
        const p2 = polar(r, endDeg);
        const largeArc = endDeg - startDeg > 180 ? 1 : 0;
        return `M ${cx} ${cy} L ${p1.x} ${p1.y} A ${r} ${r} 0 ${largeArc} 1 ${p2.x} ${p2.y} Z`;
    };

    const spin = () => {
        if (spinning) return;
        setLanded(false);

        const fullSpins =
            Math.floor(Math.random() * (spinsMax - spinsMin + 1)) + spinsMin;

        const targetIndex = forceWin
            ? rsvpIndex
            : Math.floor(Math.random() * sections.length);

        const targetAngle = (360 - (targetIndex * sliceAngle + sliceAngle / 2)) % 360;
        const currentMod = ((rotation % 360) + 360) % 360;
        const delta = (360 - currentMod + targetAngle) % 360;

        const final = rotation + fullSpins * 360 + delta + alignBias;

        setSpinning(true);
        setRotation(final);
    };

    const onTransitionEnd = (e) => {
        if (e.propertyName !== "transform") return;
        setSpinning(false);
        setLanded(true);
        if (onComplete) onComplete();
    };

    const onHitKey = (e) => {
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            spin();
        }
    };

    return (
        <section className="rwheel" aria-label="Spin to RSVP">
            <div className="rwheel__card" style={{ "--wheel-size": `${size}px`, "--spin-dur": `${spinDuration}s` }}>
                {/* Fixed-size WRAP so center never shifts */}
                <div className="rwheel__wrap">
                    {/* Fixed pointer anchored to the wrap */}
                    <div className="rwheel__pointer" aria-hidden="true" />

                    {/* Visual wheel (non-interactive) */}
                    <svg
                        className={`rwheel__svg ${spinning ? "is-spinning" : ""}`}
                        width={size}
                        height={size}
                        viewBox={`0 0 ${size} ${size}`}
                        style={{ transform: `rotate(${rotation}deg)` }}
                        onTransitionEnd={onTransitionEnd}
                        focusable="false"
                        aria-hidden="true"
                    >
                        {/* outer ring */}
                        <circle cx={cx} cy={cy} r={r + 1} className="rwheel__ring" fill="#fff" />

                        {/* slices */}
                        {sections.map((label, i) => {
                            const start = i * sliceAngle - 90;
                            const end = start + sliceAngle;
                            const isRSVP = i === rsvpIndex;
                            const path = wedgePath(start, end);

                            const mid = start + sliceAngle / 2;
                            const textR = r * 0.62;
                            const pos = polar(textR, mid);
                            const needsFlip = mid > 90 && mid < 270;
                            const rotate = needsFlip ? mid + 180 : mid;

                            return (
                                <g key={i}>
                                    <path d={path} className={`rwheel__wedge ${isRSVP ? "is-rsvp" : ""}`} />
                                    <text
                                        x={pos.x}
                                        y={pos.y}
                                        className={`rwheel__label ${isRSVP ? "is-rsvp" : ""}`}
                                        textAnchor="middle"
                                        dominantBaseline="middle"
                                        transform={`rotate(${rotate} ${pos.x} ${pos.y})`}
                                    >
                                        {label}
                                    </text>
                                </g>
                            );
                        })}

                        {/* center cap */}
                        <circle cx={cx} cy={cy} r={44} className="rwheel__cap" />
                    </svg>

                    {/* Always-visible SPIN text, centered on the WRAP */}
                    <div className="rwheel__spinText">SPIN</div>

                    {/* Invisible hit area for click/keyboard */}
                    <button
                        type="button"
                        className="rwheel__hit"
                        onClick={spin}
                        onKeyDown={onHitKey}
                        aria-label="Spin the wheel"
                        disabled={spinning}
                    />
                </div>

                {/* CTA under the fixed-size wrap (no layout shift of SPIN) */}
                {landed && (
                    <div className="rwheel__cta" aria-live="polite">
                        <ButtonComponent as="a" href={to}>RSVP Now</ButtonComponent>
                    </div>
                )}
            </div>
        </section>
    );
};

export default RsvpSpinComponent;
